// Phase 4c.7 — cartesian tile. V-shape-native: resolves every series'
// `:x`/`:y` `ColumnRef` to columns, merges them into a recharts data
// array keyed by x, and renders with the same recharts primitives the
// prior panel used (Line/Bar/Area + reference markers). Colours map
// through the existing `mantineColorVar` helper.

import dayjs from 'dayjs';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { type AspectPolicy, useAspectStyle } from '#core/components/dashboard/dashboard-grid.utils.ts';
import type { CartesianSpec, SeriesSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { mantineColorVar } from '#core/lib/formatters/colors.ts';
import { formatNumber, inferNumberFormat } from '#core/lib/formatters/number-decimals.ts';

import classes from './CartesianPanel.module.css';
import { cellScalar, dashArray, mantineColorOf } from './cell.ts';
import { useResolvedColumns } from './useResolvedColumns.ts';

// recharts series component per `:type`. `dot` is a Line with markers
// only (no connecting stroke) — recharts has no dedicated dot series.
const SERIES_COMPONENT = { line: Line, area: Area, bar: Bar, dot: Line } as const;

export function CartesianPanel({ spec }: { spec: CartesianSpec }) {
  // Resolve every series x/y ref AND every band x/lower/upper ref in one
  // stable-length call — the hook count must not depend on how many
  // series or bands a tile has (React rules-of-hooks).
  const seriesRefN = spec.series.length * 2;
  const cols = useResolvedColumns([
    ...spec.series.flatMap((s) => [s.x, s.y]),
    ...spec.bands.flatMap((b) => [b.x, b.lower, b.upper]),
  ]);
  const isTime = spec.xKind === 'time';
  const isNumeric = spec.xKind === 'numeric';
  const xKeyOf = (raw: number | string) => (isTime ? dayjs(raw).valueOf() : raw);
  const bandKey = (j: number) => `__band${j}`;

  // Merge all series + bands into rows keyed by the shared x value. A
  // band contributes a `[lower, upper]` tuple — recharts renders a range
  // Area natively when a datum's value is a two-element array.
  const byX = new Map<number | string, Record<string, number | string | null | readonly number[]>>();
  spec.series.forEach((s, i) => {
    const xs = cols[2 * i] ?? [];
    const ys = cols[2 * i + 1] ?? [];
    xs.forEach((xv, r) => {
      const rawX = cellScalar(xv);
      if (rawX === null) {
        return;
      }
      const xKey = xKeyOf(rawX);
      const row = byX.get(xKey) ?? { x: xKey };
      row[s.id] = cellScalar(ys[r]);
      byX.set(xKey, row);
    });
  });
  spec.bands.forEach((_b, j) => {
    const off = seriesRefN + 3 * j;
    const xs = cols[off] ?? [];
    const los = cols[off + 1] ?? [];
    const his = cols[off + 2] ?? [];
    xs.forEach((xv, r) => {
      const rawX = cellScalar(xv);
      if (rawX === null) {
        return;
      }
      const lo = cellScalar(los[r]);
      const hi = cellScalar(his[r]);
      const xKey = xKeyOf(rawX);
      const row = byX.get(xKey) ?? { x: xKey };
      row[bandKey(j)] = typeof lo === 'number' && typeof hi === 'number' ? [lo, hi] : null;
      byX.set(xKey, row);
    });
  });
  const data = [...byX.values()].sort((a, b) =>
    typeof a.x === 'number' && typeof b.x === 'number' ? a.x - b.x : String(a.x).localeCompare(String(b.x)),
  );
  const aspectStyle = useAspectStyle(aspectPolicy);
  const combinedValues = data.flatMap((row) =>
    spec.series.map((s) => row[s.id]).filter((v): v is number => typeof v === 'number'),
  );
  const valueFormatter = (value: number | string | readonly (string | number)[] | undefined): string => {
    if (value === undefined) {
      return 'n/a';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(valueFormatter).join(', ');
    }
    return formatNumber(value as number, { ...inferNumberFormat(combinedValues), useThousandSeparator: true });
  };
  // x-axis tick/label formatter is x-kind aware: time → date, numeric →
  // number, category → string. (Previously every number was date-formatted.)
  const labelFormatter = (value: number | string | React.ReactNode | undefined) => {
    if (value === undefined) {
      return 'n/a';
    }
    if (typeof value === 'number') {
      return isTime
        ? dayjs(value).format('YY-MM-DD')
        : formatNumber(value, { ...inferNumberFormat([value]), useThousandSeparator: true });
    }
    return value?.toString() ?? 'n/a';
  };

  const hasRightAxis = spec.series.some((s) => s.axis === 'right');
  const stacked = spec.stack !== 'none';
  const horizontal = spec.orientation === 'horizontal';

  // Axis title objects. A label belongs to the *logical* axis (x =
  // category, y = value); which physical recharts axis carries it
  // depends on orientation.
  const xTitle = (text: string | undefined) =>
    text === undefined
      ? undefined
      : {
          value: text,
          position: 'insideBottom' as const,
          offset: -4,
          fontSize: 'var(--mantine-font-size-xs)',
          fill: mantineColorVar('dark', 1),
        };
  const yTitle = (text: string | undefined, side: 'left' | 'right') =>
    text === undefined
      ? undefined
      : {
          value: text,
          angle: -90,
          position: side === 'right' ? ('insideRight' as const) : ('insideLeft' as const),
          style: { textAnchor: 'middle' as const },
          fontSize: 'var(--mantine-font-size-xs)',
          fill: mantineColorVar('dark', 1),
        };

  // recharts coordinate props for a reference anchored to a logical axis.
  // `y` = value axis (always the left value axis — refs carry no side);
  // `x` = the shared category/x axis. Swaps with chart orientation.
  const refLine = (axis: 'x' | 'y', value: number) =>
    axis === 'y'
      ? horizontal
        ? { x: value, xAxisId: 'left' as const }
        : { y: value, yAxisId: 'left' as const }
      : horizontal
        ? { y: value, yAxisId: CATEGORY_AXIS }
        : { x: value };
  const refArea = (axis: 'x' | 'y', start: number, end: number) =>
    axis === 'y'
      ? horizontal
        ? { x1: start, x2: end, xAxisId: 'left' as const }
        : { y1: start, y2: end, yAxisId: 'left' as const }
      : horizontal
        ? { y1: start, y2: end, yAxisId: CATEGORY_AXIS }
        : { x1: start, x2: end };

  const categoryAxis = horizontal ? (
    <YAxis
      axisLine={false}
      dataKey="x"
      fontSize={12}
      fontWeight={500}
      label={yTitle(spec.xLabel, 'left')}
      stroke={mantineColorVar('dark', 0)}
      tickFormatter={labelFormatter}
      type="category"
      width="auto"
      yAxisId={CATEGORY_AXIS}
    />
  ) : (
    <XAxis
      axisLine={false}
      dataKey="x"
      fontSize={12}
      fontWeight={500}
      label={xTitle(spec.xLabel)}
      minTickGap={10}
      stroke={mantineColorVar('dark', 0)}
      tickFormatter={labelFormatter}
      type={isNumeric ? 'number' : 'category'}
    />
  );

  // Value axis (left always; right only when a series binds to it). It is
  // the Y axis in the default vertical orientation, the X axis when the
  // chart is horizontal.
  const valueAxis = (side: 'left' | 'right') => {
    const text = side === 'left' ? spec.yLabelLeft : spec.yLabelRight;
    const common = {
      axisLine: false as const,
      fontSize: 12,
      fontWeight: 500 as const,
      stroke: mantineColorVar('dark', 0),
      tickFormatter: valueFormatter,
      width: 'auto' as const,
    };
    return horizontal ? (
      <XAxis key={side} {...common} label={xTitle(text)} type="number" xAxisId={side} />
    ) : (
      <YAxis key={side} {...common} label={yTitle(text, side)} orientation={side} yAxisId={side} />
    );
  };

  return (
    <div className={classes.cartesianChart} style={aspectStyle}>
      <ComposedChart
        className={classes.chart}
        data={data}
        height="100%"
        layout={horizontal ? 'vertical' : 'horizontal'}
        responsive
        stackOffset={spec.stack === 'percent' ? 'expand' : 'none'}
        width="100%"
      >
        <CartesianGrid
          horizontal={!horizontal}
          stroke={mantineColorVar('dark', 5)}
          strokeOpacity={0.5}
          vertical={horizontal}
        />
        {categoryAxis}
        {valueAxis('left')}
        {hasRightAxis && valueAxis('right')}
        <Legend
          align="right"
          formatter={(value) => <span style={{ fontSize: 'var(--mantine-font-size-sm)' }}>{value}</span>}
          iconSize={8}
          verticalAlign="bottom"
        />
        <Tooltip
          allowEscapeViewBox={{ x: false, y: false }}
          contentStyle={undefined}
          cursor={{ stroke: 'var(--mantine-color-dark-2)', strokeOpacity: 0.5 }}
          formatter={valueFormatter}
          isAnimationActive={false}
          itemStyle={undefined}
          labelFormatter={labelFormatter}
          labelStyle={undefined}
          separator={undefined}
          wrapperClassName={classes.tooltip}
          wrapperStyle={undefined}
        />
        {/* Bands paint first so they sit behind every series. */}
        {spec.bands.map((b, j) => {
          const bandColor = mantineColorOf(b.color);
          const valueSide = b.axis ?? 'left';
          const axisIds = horizontal ? { xAxisId: valueSide, yAxisId: CATEGORY_AXIS } : { yAxisId: valueSide };
          return (
            <Area
              activeDot={false}
              connectNulls={false}
              dataKey={bandKey(j)}
              dot={false}
              fill={mantineColorVar(bandColor, 6)}
              fillOpacity={0.18}
              isAnimationActive={false}
              key={`band-${b.lower.column}-${b.upper.column}-${b.label ?? ''}`}
              legendType="none"
              name={b.label ?? 'band'}
              stroke="none"
              {...axisIds}
            />
          );
        })}
        {spec.series.map((s: SeriesSpec) => {
          const renderType = spec.series.length === 1 && (s.type === 'line' || !s.type) ? 'area' : (s.type ?? 'line');
          const SeriesComponent = SERIES_COMPONENT[renderType];
          const color = mantineColorOf(s.color);
          const valueSide = s.axis ?? 'left';
          const axisIds = horizontal ? { xAxisId: valueSide, yAxisId: CATEGORY_AXIS } : { yAxisId: valueSide };
          const isDot = s.type === 'dot';
          // Lines never stack; bars/areas share a stackId per value axis.
          const stackId = stacked && (renderType === 'bar' || renderType === 'area') ? `stack-${valueSide}` : undefined;

          return (
            <SeriesComponent
              activeDot={{
                r: 4,
                fill: mantineColorVar(color, 9),
                fillOpacity: 1,
                strokeWidth: 1.5,
                stroke: mantineColorVar(color, 3),
              }}
              connectNulls={false}
              dataKey={s.id}
              dot={
                isDot
                  ? { r: 3, fill: mantineColorVar(color, 9), fillOpacity: 1, strokeWidth: 0 }
                  : { r: 2, fill: mantineColorVar(color, 9), fillOpacity: 1, strokeWidth: 1.5 }
              }
              fill={mantineColorVar(color, 8)}
              fillOpacity={fillOpacity.light}
              isAnimationActive={false}
              key={s.id}
              legendType="square"
              name={s.label}
              stroke={isDot ? 'none' : mantineColorVar(color, 3)}
              strokeDasharray={dashArray(s.stroke)}
              strokeWidth={1.5}
              type={CURVE_RECHARTS[s.curve]}
              {...(stackId === undefined ? {} : { stackId })}
              {...axisIds}
            />
          );
        })}
        {spec.referenceLines.map((rl) => (
          <ReferenceLine
            key={`rl-${rl.axis}-${rl.value}-${rl.label ?? ''}`}
            label={rl.label}
            stroke={mantineColorVar(mantineColorOf(rl.color), 6)}
            strokeDasharray={dashArray(rl.style)}
            {...refLine(rl.axis, rl.value)}
          />
        ))}
        {spec.referenceAreas.map((ra) => (
          <ReferenceArea
            fill={mantineColorVar(mantineColorOf(ra.color), 6)}
            fillOpacity={0.12}
            key={`ra-${ra.axis}-${ra.start}-${ra.end}-${ra.label ?? ''}`}
            label={ra.label}
            {...refArea(ra.axis, ra.start, ra.end)}
          />
        ))}
      </ComposedChart>
    </div>
  );
}

/** recharts axis id for the shared category/x axis. */
const CATEGORY_AXIS = 'category';

/** Dashboard `Curve` → recharts line/area interpolation `type`. */
const CURVE_RECHARTS = { linear: 'linear', smooth: 'monotone', step: 'step' } as const;
const fillOpacity = {
  light: 0.15,
  medium: 0.3,
  strong: 0.6,
  none: 0,
} as const;

const aspectPolicy: AspectPolicy = {
  preferredAspect: 2,
  minAspect: 1.5,
  maxAspect: 3.0,
  minHeight: 300,
  maxHeight: 400,
} as const;
